/**
 * /purchase-orders — Purchase Orders list page.
 *
 * Status filter tabs: All / Open (draft+pending+approved+issued) /
 * Receiving / Closed · Cancelled
 *
 * Grid: PO #, Type, Vendor, Project, Status, Issue date, Exp. delivery, Total
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Tab, Tabs } from "@mui/material";

import { POGrid } from "@/grids/POGrid";
import {
  partiesListQueryOptions,
  projectsListQueryOptions,
  purchaseOrdersListQueryOptions,
} from "@/lib/queries";
import type { Party, ProjectSummary, PurchaseOrder, POStatus } from "@/lib/types";

export const Route = createFileRoute("/_auth/purchase-orders/")({
  component: PurchaseOrdersIndexPage,
});

type TabKey = "all" | "open" | "receiving" | "closed";

const TAB_STATUSES: Record<TabKey, POStatus[] | null> = {
  all: null,
  open: ["draft", "pending_approval", "approved", "issued"],
  receiving: ["partially_received", "fully_received"],
  closed: ["closed", "cancelled"],
};

function PurchaseOrdersIndexPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("open");

  const posQ = useQuery(purchaseOrdersListQueryOptions({}));
  const vendorsQ = useQuery(partiesListQueryOptions({ party_type: "vendor" }));
  const projectsQ = useQuery(projectsListQueryOptions({ limit: 500 }));

  const vendorsById = useMemo(() => {
    const m: Record<string, Party> = {};
    (vendorsQ.data ?? []).forEach((v) => { m[v.id] = v; });
    return m;
  }, [vendorsQ.data]);

  const projectsById = useMemo(() => {
    const m: Record<string, ProjectSummary> = {};
    (projectsQ.data?.items ?? []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [projectsQ.data?.items]);

  const allPos = posQ.data ?? [];

  const filtered = useMemo<PurchaseOrder[]>(() => {
    const statuses = TAB_STATUSES[tab];
    if (!statuses) return allPos;
    return allPos.filter((po) => statuses.includes(po.status as POStatus));
  }, [allPos, tab]);

  const counts: Record<TabKey, number> = useMemo(() => ({
    all: allPos.length,
    open: allPos.filter((po) => TAB_STATUSES.open!.includes(po.status as POStatus)).length,
    receiving: allPos.filter((po) => TAB_STATUSES.receiving!.includes(po.status as POStatus)).length,
    closed: allPos.filter((po) => TAB_STATUSES.closed!.includes(po.status as POStatus)).length,
  }), [allPos]);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Purchase Orders</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Click a PO # to open · all vendors · all projects
          </span>
          <div className="flex-1" />
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate({ to: "/purchase-orders/new" })}
            title="Create a new purchase order"
          >
            + New PO
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          sx={{ minHeight: 40 }}
        >
          <Tab value="all" label={`All (${counts.all})`} sx={{ textTransform: "none", minHeight: 40, fontSize: 13 }} />
          <Tab value="open" label={`Open (${counts.open})`} sx={{ textTransform: "none", minHeight: 40, fontSize: 13 }} />
          <Tab value="receiving" label={`Receiving (${counts.receiving})`} sx={{ textTransform: "none", minHeight: 40, fontSize: 13 }} />
          <Tab value="closed" label={`Closed (${counts.closed})`} sx={{ textTransform: "none", minHeight: 40, fontSize: 13 }} />
        </Tabs>
      </div>

      <div className="flex-1 px-6 py-4 overflow-hidden">
        <POGrid
          pos={filtered}
          vendorsById={vendorsById}
          projectsById={projectsById}
          loading={posQ.isLoading || vendorsQ.isLoading}
        />
      </div>
    </div>
  );
}
